import { openPatientQuickView } from '@/components/admin/PatientQuickViewDialog';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, Calendar, X, CheckCheck, UserPlus, MapPin, Star, Volume2, VolumeX, UserX, Scissors, AlertTriangle, MessageCircle, CreditCard, ShieldAlert, HeartPulse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { notificationsAPI } from '@/lib/api';
import {
  REENGAGEMENT_TEMPLATES,
  classifyReason,
  firstName,
  buildWaUrl,
} from '@/lib/reengagementTemplates';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const NOTIFICATION_SOUND_KEY = 'notification_sound_enabled';

const matchType = (...types) => (n) => types.includes(n.type);
const BELL_FILTERS = [
  { key: 'all', label: 'Todas', match: () => true },
  { key: 'new_appointment', label: 'Citas', icon: UserPlus, match: matchType('new_appointment') },
  { key: 'confirmed', label: 'Confirmadas', icon: CheckCheck, match: (n) => n.type === 'patient_appointment_change' && n.data?.action === 'confirmado' },
  { key: 'cancelled_appointment', label: 'Canceladas', icon: UserX, match: (n) => n.type === 'cancelled_appointment' || (n.type === 'patient_appointment_change' && n.data?.action === 'cancelado') },
  { key: 'postop', label: 'Post-op', icon: HeartPulse, match: matchType('postop_alarm', 'postop_survey_low') },
  { key: 'new_surgery', label: 'Cirugías', icon: Scissors, match: matchType('new_surgery') },
  { key: 'sms_undelivered', label: 'SMS fallidos', icon: AlertTriangle, match: matchType('sms_undelivered') },
];
const BELL_FILTERS_ADMIN = [
  { key: 'review_confirmed', label: 'Reseñas', icon: Star, match: matchType('review_confirmed') },
  { key: 'calendar_sync', label: 'Calendario', icon: Calendar, match: matchType('calendar_sync') },
];

import { useAuth } from '@/contexts/AuthContext';

export default function NotificationBell() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'superadmin' || admin?.role === 'admin';
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem(NOTIFICATION_SOUND_KEY);
    return saved !== 'false';
  });
  const dropdownRef = useRef(null);
  const eventSourceRef = useRef(null);
  const audioRef = useRef(null);

  // Re-engagement WhatsApp dialog state. Opened from the cancellation
  // notification recuadro. Lets the doctor tweak the suggested message
  // before opening wa.me (avoids needing 1 Meta template per category).
  const [waDialog, setWaDialog] = useState({
    open: false,
    patientName: '',
    patientPhone: '',
    reason: '',
    category: 'Sin motivo indicado',
  });
  const [waMessage, setWaMessage] = useState('');

  const openWhatsAppDialog = (data) => {
    const cat = classifyReason(data?.cancellation_reason || '');
    const tpl = REENGAGEMENT_TEMPLATES[cat] || REENGAGEMENT_TEMPLATES['Otro'];
    const fname = firstName(data?.patient_name);
    setWaDialog({
      open: true,
      patientName: data?.patient_name || '',
      patientPhone: data?.patient_phone || '',
      reason: data?.cancellation_reason || '',
      category: cat,
    });
    setWaMessage(tpl(fname));
  };

  const applyTemplate = (cat) => {
    setWaDialog((d) => ({ ...d, category: cat }));
    const tpl = REENGAGEMENT_TEMPLATES[cat] || REENGAGEMENT_TEMPLATES['Otro'];
    setWaMessage(tpl(firstName(waDialog.patientName)));
  };

  const sendViaWhatsApp = () => {
    if (!waDialog.patientPhone) {
      toast.error('Esta paciente no tiene teléfono registrado');
      return;
    }
    if (!waMessage.trim()) {
      toast.error('Escriba un mensaje antes de enviar');
      return;
    }
    window.open(buildWaUrl(waDialog.patientPhone, waMessage), '_blank', 'noopener,noreferrer');
    setWaDialog((d) => ({ ...d, open: false }));
  };
  // Tracks the timestamp of the last SSE event of any kind (connected /
  // ping / new_notification). A heartbeat watchdog uses it to detect
  // silently-dead connections that never fire `onerror` (a known
  // EventSource failure mode behind ingress proxies and during server
  // restarts) and force a clean reconnect.
  const lastSseActivityRef = useRef(Date.now());
  const navigate = useNavigate();

  // Initialize audio
  useEffect(() => {
    // Create a simple notification sound using Web Audio API
    audioRef.current = {
      play: () => {
        if (!soundEnabled) return;
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          oscillator.frequency.value = 830;
          oscillator.type = 'sine';
          gainNode.gain.value = 0.15;
          gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
          oscillator.start(ctx.currentTime);
          oscillator.stop(ctx.currentTime + 0.5);
          // Second tone
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.frequency.value = 1050;
          osc2.type = 'sine';
          gain2.gain.value = 0.15;
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
          osc2.start(ctx.currentTime + 0.15);
          osc2.stop(ctx.currentTime + 0.7);
        } catch (e) {
          // Audio not available, ignore
        }
      }
    };
  }, [soundEnabled]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Stable loaders (useCallback) so the visibility / focus / polling
  // effects below can depend on them without retriggering on every render.
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll(false, 50);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Force the SSE effect below to tear down + reconnect. Used by the
  // heartbeat watchdog and visibility/focus handlers when the connection
  // looks stale.
  const triggerSseReconnect = useCallback(() => {
    setSseReconnectKey(k => k + 1);
    // Hard close any existing source so the cleanup below fires.
    if (eventSourceRef.current) {
      try { eventSourceRef.current.close(); } catch { /* noop */ }
      eventSourceRef.current = null;
    }
    setSseConnected(false);
  }, []);

  // Load initial unread count
  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  // SSE Connection. We re-run on every tick of `sseReconnectKey`.
  const [sseReconnectKey, setSseReconnectKey] = useState(0);

  // Bump the reconnect key when triggerSseReconnect mutates the ref.
  // Wrapping in a useCallback keeps the reference stable.
  const requestSseReconnect = useCallback(() => {
    triggerSseReconnect();
    setSseReconnectKey(k => k + 1);
  }, [triggerSseReconnect]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let retryTimeout;
    let retryCount = 0;
    const maxRetryDelay = 30000;

    const connectSSE = () => {
      const url = notificationsAPI.getStreamUrl();
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.addEventListener('connected', () => {
        setSseConnected(true);
        lastSseActivityRef.current = Date.now();
        retryCount = 0;
      });

      es.addEventListener('new_notification', (event) => {
        lastSseActivityRef.current = Date.now();
        try {
          const notification = JSON.parse(event.data);
          // Update state
          setNotifications(prev => [notification, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + 1);
          // Play sound
          audioRef.current?.play();
          // Show toast
          toast(notification.title, {
            description: notification.message,
            duration: 5000,
          });
        } catch (e) {
          console.error('Error parsing SSE notification:', e);
        }
      });

      es.addEventListener('ping', () => {
        // Keepalive — reset the watchdog clock so we don't reconnect for
        // nothing on quiet hours.
        lastSseActivityRef.current = Date.now();
      });

      es.onerror = () => {
        setSseConnected(false);
        es.close();
        // Exponential backoff reconnect
        const delay = Math.min(1000 * Math.pow(2, retryCount), maxRetryDelay);
        retryCount++;
        retryTimeout = setTimeout(connectSSE, delay);
      };
    };

    connectSSE();

    return () => {
      clearTimeout(retryTimeout);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
    // sseReconnectKey is the explicit reconnection trigger.
  }, [sseReconnectKey]);

  // ─────────────────────────────────────────────────────────────────────
  // Resilience layer — defends against the symptom "I have to click the
  // bell for it to update". Four overlapping mechanisms, each cheap:
  //
  //   (a) Heartbeat watchdog: every 15s check that we've heard *something*
  //       from the SSE within the last 60s. If not, the connection is
  //       almost certainly dead even though `onerror` never fired (a
  //       known EventSource failure mode behind some ingress proxies).
  //       Force a hard reconnect.
  //
  //   (b) Tab visibility: when the user comes back to the tab, refresh
  //       the count and re-establish SSE if it looks stale. Catches the
  //       "left it on another tab for 20 min" scenario.
  //
  //   (c) Window focus: defensive duplicate of (b) for browsers that
  //       don't fire visibilitychange reliably (some Safari versions).
  //
  //   (d) Safety-net polling at 60s, ALWAYS on. Confirms the count even
  //       when SSE is reportedly fine. Overhead is one tiny GET per
  //       minute — negligible.
  // ─────────────────────────────────────────────────────────────────────

  // (a) Heartbeat watchdog
  useEffect(() => {
    const HEARTBEAT_TIMEOUT_MS = 60_000;
    const CHECK_INTERVAL_MS = 15_000;
    const id = setInterval(() => {
      if (!eventSourceRef.current) return;
      const silence = Date.now() - lastSseActivityRef.current;
      if (silence > HEARTBEAT_TIMEOUT_MS) {
        // Connection appears dead — force reconnect and refresh count
        // from REST so the UI is correct in the meantime.
        requestSseReconnect();
        loadUnreadCount();
      }
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [requestSseReconnect, loadUnreadCount]);

  // (b) + (c) Tab visibility / window focus
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      loadUnreadCount();
      // If SSE looks idle (>10s) when the user returns, reconnect proactively.
      if (Date.now() - lastSseActivityRef.current > 10_000) {
        requestSseReconnect();
      }
    };
    const onFocus = () => {
      loadUnreadCount();
      if (Date.now() - lastSseActivityRef.current > 10_000) {
        requestSseReconnect();
      }
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadUnreadCount, requestSseReconnect]);

  // (d) Safety-net polling — ALWAYS on, 60s
  useEffect(() => {
    const id = setInterval(loadUnreadCount, 60_000);
    return () => clearInterval(id);
  }, [loadUnreadCount]);

  // (e) Realtime: when AdminLayout's global agenda SSE fires, refresh the bell
  // counter immediately so the patient action is visible without the 60s poll.
  useEffect(() => {
    const handler = () => loadUnreadCount();
    window.addEventListener('agenda:event', handler);
    return () => window.removeEventListener('agenda:event', handler);
  }, [loadUnreadCount]);

  const handleToggle = () => {
    if (!isOpen) {
      loadNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.delete(notificationId);
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const newVal = !soundEnabled;
    setSoundEnabled(newVal);
    localStorage.setItem(NOTIFICATION_SOUND_KEY, String(newVal));
  };

  const getNotificationIcon = (type, data) => {
    switch (type) {
      case 'calendar_sync':
        return <Calendar className="w-5 h-5 text-brand-navy" />;
      case 'new_appointment':
        return <UserPlus className="w-5 h-5 text-emerald-500" />;
      case 'cancelled_appointment':
        return <UserX className="w-5 h-5 text-red-500" />;
      case 'patient_appointment_change':
        if (data?.action === 'cancelado') return <UserX className="w-5 h-5 text-red-500" />;
        if (data?.action === 'reprogramado') return <Calendar className="w-5 h-5 text-sky-600" />;
        return <CheckCheck className="w-5 h-5 text-emerald-600" />;
      case 'postop_alarm':
        return <HeartPulse className="w-5 h-5 text-red-600" />;
      case 'postop_survey_low':
        return <Star className="w-5 h-5 text-amber-500" />;
      case 'review_confirmed':
        return <Star className="w-5 h-5 text-brand-deep" />;
      case 'new_surgery':
        return <Scissors className="w-5 h-5 text-brand-deep" />;
      case 'sms_undelivered':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'stripe_payment':
        return <CreditCard className="w-5 h-5 text-emerald-600" />;
      case 'security_alert':
        return <ShieldAlert className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead({ stopPropagation: () => {} }, notification.id);
    }

    if ((notification.type === 'new_appointment' || notification.type === 'cancelled_appointment') && notification.data) {
      const params = new URLSearchParams({
        view: 'worklist',
        date: notification.data.date || '',
        locationId: notification.data.location_id || ''
      });
      navigate(`/admin/agenda?${params.toString()}`);
      setIsOpen(false);
    } else if (notification.type === 'review_confirmed') {
      navigate('/admin/reviews');
      setIsOpen(false);
    } else if (notification.type === 'new_surgery' && notification.data) {
      const params = new URLSearchParams({ date: notification.data.date || '' });
      navigate(`/admin/surgeries?${params.toString()}`);
      setIsOpen(false);
    } else if (notification.type === 'sms_undelivered' && notification.data?.patient_id) {
      openPatientQuickView(notification.data.patient_id);
      setIsOpen(false);
    } else if (['patient_appointment_change', 'postop_alarm', 'postop_survey_low'].includes(notification.type) && notification.data?.patient_id) {
      openPatientQuickView(notification.data.patient_id);
      setIsOpen(false);
    } else if (notification.type === 'stripe_payment') {
      // Click en pago recibido → ficha del paciente (bloque "Pagos" visible)
      if (notification.data?.patient_id) {
        openPatientQuickView(notification.data.patient_id);
      } else {
        navigate('/admin/pagos-stripe');
      }
      setIsOpen(false);
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: es
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors group"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 transition-colors ${unreadCount > 0 ? 'text-brand-navy' : 'text-gray-600'}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* SSE status dot */}
        <span
          className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${sseConnected ? 'bg-emerald-400' : 'bg-gray-300'}`}
          title={sseConnected ? 'Conectado en tiempo real' : 'Reconectando...'}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed inset-x-2 top-16 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-[70vh] sm:max-h-[500px] flex flex-col"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Notificaciones</h3>
              {sseConnected && (
                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                  EN VIVO
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSound}
                className="p-1 hover:bg-gray-100 rounded"
                title={soundEnabled ? 'Silenciar alertas' : 'Activar alertas'}
                data-testid="notification-sound-toggle"
              >
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-gray-500" />
                ) : (
                  <VolumeX className="w-4 h-4 text-gray-400" />
                )}
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-brand-navy hover:text-brand-navy flex items-center gap-1"
                  title="Marcar todas como leídas"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4" />
                  <span>Marcar todas</span>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-1 px-3 py-2 border-b bg-gray-50/50" data-testid="notification-filters">
            {[...BELL_FILTERS, ...(isAdmin ? BELL_FILTERS_ADMIN : [])].map(f => {
              const count = f.key === 'all'
                ? notifications.length
                : notifications.filter(f.match).length;
              return (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    activeFilter === f.key
                      ? 'bg-brand-navy text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.icon && <f.icon size={12} />}
                  <span>{f.label}</span>
                  {count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0 rounded-full text-[10px] ${
                      activeFilter === f.key ? 'bg-white/20' : 'bg-gray-200'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {(() => {
              const active = [...BELL_FILTERS, ...BELL_FILTERS_ADMIN].find(f => f.key === activeFilter);
              const filtered = (!active || active.key === 'all')
                ? notifications
                : notifications.filter(active.match);
              return loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-brand-navy border-t-transparent rounded-full mx-auto mb-2" />
                Cargando...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{activeFilter === 'all' ? 'No hay notificaciones' : 'Sin notificaciones de este tipo'}</p>
              </div>
            ) : (
              <div className="divide-y">
                {filtered.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.read ? 'bg-brand-soft/30/70' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type, notification.data)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-gray-900`}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5">
                              {notification.message}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.read && (
                              <button
                                onClick={(e) => handleMarkAsRead(e, notification.id)}
                                className="p-1 hover:bg-gray-200 rounded"
                                title="Marcar como leída"
                              >
                                <Check className="w-4 h-4 text-green-500" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(e, notification.id)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>

                        {/* Calendar sync details */}
                        {notification.type === 'calendar_sync' && notification.data?.sync_result && (
                          <div className="mt-2 flex gap-3 text-xs">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              {notification.data.sync_result.created} nuevas
                            </span>
                            <span className="px-2 py-0.5 bg-brand-soft/60 text-brand-navy rounded">
                              {notification.data.sync_result.updated} actualizadas
                            </span>
                          </div>
                        )}

                        {/* New appointment details */}
                        {notification.type === 'new_appointment' && notification.data && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                            {notification.data.location_name && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">
                                <MapPin size={10} />
                                {notification.data.location_name}
                              </span>
                            )}
                            {notification.data.pending_verification && (
                              <button
                                onClick={(e) => {
                                  handleMarkAsRead(e, notification.id);
                                  setIsOpen(false);
                                  navigate(`/admin/patient-verification${notification.data.patient_id ? `?patient=${notification.data.patient_id}` : ''}`);
                                }}
                                className="px-2.5 py-1 bg-amber-500 text-white rounded-md font-semibold hover:bg-amber-600 shadow-sm"
                                data-testid="verify-pending-patient-btn"
                              >
                                ⚠️ Verificar ficha
                              </button>
                            )}
                            <span className="text-brand-navy underline text-xs">
                              Ver en agenda
                            </span>
                          </div>
                        )}

                        {/* Cancelled appointment details */}
                        {notification.type === 'cancelled_appointment' && notification.data && (
                          <div className="mt-2 space-y-1.5">
                            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                              {notification.data.patient_name && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 rounded font-medium">
                                  <UserX size={10} />
                                  {notification.data.patient_name}
                                </span>
                              )}
                              {notification.data.patient_phone && (
                                <a
                                  href={`tel:${notification.data.patient_phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 hover:underline"
                                  data-testid="cancel-patient-phone"
                                >
                                  📞 {notification.data.patient_phone}
                                </a>
                              )}
                              {notification.data.patient_email && (
                                <a
                                  href={`mailto:${notification.data.patient_email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded hover:bg-sky-100 hover:underline truncate max-w-[180px]"
                                  data-testid="cancel-patient-email"
                                  title={notification.data.patient_email}
                                >
                                  ✉ {notification.data.patient_email}
                                </a>
                              )}
                              {notification.data.patient_dni && (
                                <span
                                  className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                                  title="DNI / Pasaporte"
                                >
                                  {notification.data.patient_dni}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                              {notification.data.location_name && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded">
                                  <MapPin size={10} />
                                  {notification.data.location_name}
                                </span>
                              )}
                              {notification.data.start_time && notification.data.end_time && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                  {notification.data.start_time} - {notification.data.end_time}
                                </span>
                              )}
                              <span className="text-brand-navy underline text-xs">
                                Ver en agenda
                              </span>
                            </div>
                            {notification.data.cancellation_reason && (
                              <div
                                className="mt-1 rounded-md border-l-4 border-brand-medium bg-brand-soft/40 px-2.5 py-1.5 text-[11px] text-brand-deep"
                                data-testid="cancel-reason"
                              >
                                <span className="font-semibold uppercase tracking-wide text-[9px] text-brand-deep block leading-none mb-0.5">
                                  Motivo indicado
                                </span>
                                <span className="italic leading-snug">
                                  {notification.data.cancellation_reason}
                                </span>
                              </div>
                            )}
                            {notification.data.patient_phone && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openWhatsAppDialog(notification.data);
                                }}
                                className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                                data-testid="reengage-whatsapp-btn"
                              >
                                <MessageCircle size={12} />
                                WhatsApp con propuesta
                              </button>
                            )}
                          </div>
                        )}

                        {/* Review confirmed details */}
                        {notification.type === 'review_confirmed' && notification.data && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-brand-soft/40 text-brand-deep rounded">
                              <Star size={10} />
                              {notification.data.method === 'email' ? 'Email' : notification.data.method === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                            </span>
                            <span className="text-brand-navy underline text-xs">
                              Ver dashboard
                            </span>
                          </div>
                        )}

                        <p className="text-xs text-gray-400 mt-2">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
            })()}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t bg-gray-50 text-center">
              <a
                href="/admin/notifications"
                className="text-sm text-brand-navy hover:text-brand-navy"
              >
                Ver todo el historial
              </a>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          WhatsApp re-engagement dialog. Lets the doctor edit the
          category-tailored message before opening wa.me. No Meta
          template approval needed because the message is sent from
          the doctor's own WhatsApp app/web session.
          ───────────────────────────────────────────────────────────── */}
      <Dialog open={waDialog.open} onOpenChange={(o) => setWaDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="reengage-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <MessageCircle size={18} />
              WhatsApp con propuesta · {waDialog.patientName}
            </DialogTitle>
          </DialogHeader>

          {waDialog.reason && (
            <div className="rounded-md border-l-4 border-brand-medium bg-brand-soft/40 px-3 py-2 text-xs text-brand-deep">
              <span className="font-semibold uppercase tracking-wide text-[10px] block">Motivo de la paciente</span>
              <span className="italic">{waDialog.reason}</span>
            </div>
          )}

          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-900 flex gap-2">
            <span className="text-sm leading-none mt-0.5">💡</span>
            <span className="leading-snug">
              La lista de espera ya estará ofreciendo el hueco a otra paciente automáticamente. Este mensaje es para intentar retener a <strong>{firstName(waDialog.patientName)}</strong> ofreciéndole una alternativa (telefónica, otro horario, teleconsulta…).
            </span>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">
              Categoría detectada (puede cambiarla):
            </label>
            <select
              value={waDialog.category}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none"
              data-testid="reengage-category-select"
            >
              {Object.keys(REENGAGEMENT_TEMPLATES).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
              <span>Mensaje (editable antes de enviar)</span>
              <span className="text-[10px] text-gray-400">{waMessage.length} caracteres</span>
            </label>
            <textarea
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              rows={6}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
              data-testid="reengage-message-input"
            />
            <p className="text-[10px] text-gray-500 leading-snug">
              Al pulsar &laquo;Abrir WhatsApp&raquo; se abrirá una conversación nueva con el teléfono {waDialog.patientPhone || 'de la paciente'} y el mensaje pre-cargado. La envías tú manualmente desde tu WhatsApp.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWaDialog((d) => ({ ...d, open: false }))}
              data-testid="reengage-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={sendViaWhatsApp}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="reengage-send-btn"
            >
              <MessageCircle size={14} className="mr-1.5" />
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
